import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { logger } from '@core/helpers/logger';
import {
  AccountService,
  AuthService,
  BillingV2Service,
  ConfigService,
  ContextService,
  FilesService,
  PlatformService,
  SchoolsService,
  UserServicesService,
} from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { LibraryRouter } from '@models/library.router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationDialogsService } from '@shared/application-dialogs';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { getMe } from '@store/reducers/profile.reducer';
import {
  CSchool,
  EUserServiceType,
  IAccountStats,
  ImageSizeEnum,
  SubjectCategoryEnum,
  User,
  UserService,
  UserStatusEnum,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCover } from 'onclass-ui/lib/modules/on-ui-cover/on-ui-cover.component';
import { Observable } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ServiceCardDisplayModeType, UserServiceCardComponentMode } from './types';

const daysOfWeek = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'];

@Component({
  selector: 'app-user-service-card-2',
  templateUrl: './user-service-card-2.component.html',
  styleUrls: ['./user-service-card-2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserServiceCard2Component extends ChangableComponent implements OnInit {
  @Input() public set service(service: UserService) {
    if (!service) {
      return;
    }
    this._service = service;
    this.prepareCard();
  }
  public get service() {
    return this._service;
  }
  @Input() displayMode: ServiceCardDisplayModeType = 'wide';
  @Input() mode: UserServiceCardComponentMode;
  @Input() authorDisplay = true;
  @Output() openUserServiceInModal = new EventEmitter<UserService>();
  @Output() removed = new EventEmitter<boolean>();
  @Output() selected = new EventEmitter<boolean>();
  @Output() favoriteChanged = new EventEmitter<boolean>();
  public eUserServiceType = EUserServiceType;
  public isGuest: boolean;

  public cover: OnUiCover;
  public userAvatar: OnUiCover;
  public title: string;
  public type: EUserServiceType;
  public fullTitle: string;
  public authorUrl: string[];
  public excerpt: string;
  public coverImageUrl: string;
  public metaImageUrl: string;
  public subjectTitle: string;
  public subjectIsNative: boolean;
  public category: string;
  public discount: number;
  public duration: number;
  public price_tier: number;
  public authorStats: Partial<IAccountStats>;
  public school: CSchool;
  public userServiceRoute: string;
  public description: string;
  public contentDate: Date;
  public alertOpened: boolean;
  public age_from: number;
  public age_to: number;
  public participants_limit: number;
  public meetings_limit: number;
  public weekdays: number[];
  public debug$ = this.contextService.debug$;

  private _service: UserService;
  private _author: User;
  private _me: User;

  public constructor(
    public errorNotificationService: ErrorNotificationService,
    public deviceService: DeviceDetectorService,
    private translate: TranslateService,
    private authService: AuthService,
    private store: Store,
    private billingV2Service: BillingV2Service,
    private contextService: ContextService,
    protected dialogsService: ApplicationDialogsService,
    private userServicesService: UserServicesService,
    private schoolService: SchoolsService,
    private accountService: AccountService,
    private configService: ConfigService,
    protected cdr: ChangeDetectorRef,
    protected platform: PlatformService,
  ) {
    super(cdr, platform);
  }

  ngOnInit(): void {
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this._me = me;
          this.isGuest = me?.status === UserStatusEnum.guest;
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public prepareCard() {
    if (!this.service?.author) {
      this.accountService
        .getUserById(this._service.author_id, 'LOAD_TIME')
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (user) => {
            this._author = this.service.author = user;
            this.prepareAuthor(this._author);
            this.detectChanges();
          },
          error: (error) => {
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          },
        });
    } else {
      this._author = this.service.author;
      this.prepareAuthor(this._author);
    }

    if (!this._service.subject) {
      this.configService.subjects$.pipe(takeUntil(this.destroyed$)).subscribe({
        next: (subjects) => {
          this._service.subject = subjects.find((el) => el.id === this._service.subject_id);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
    }

    this.title = this._service?.title;
    this.type = this._service?.type;
    this.fullTitle = this.userServicesService.userServiceFullTitle(this._service);
    this.excerpt = this._service?.excerpt;
    this.description = this._service?.description;

    this.coverImageUrl = this.service?.__opts?.images.length
      ? this.prepareStorageUrl(this.service?.__opts?.images[0].md)
      : FilesService.getFileBySize(this._service?.cover, ImageSizeEnum.wide)?.url ||
        FilesService.getFileUrlBySize(this._service.cover_id, ImageSizeEnum.wide);
    this.subjectTitle = this._service?.subject?.title;
    this.subjectIsNative = this._service?.subject?.category_id === SubjectCategoryEnum.native_language;
    this.category = this._service?.category?.title;
    this.discount = this._service?.discount;
    this.duration = this._service?.duration;
    this.price_tier = this._service?.price_tier;
    this.userServiceRoute = LibraryRouter.userServiceRouteUniversal(this._service);
    this.age_from = this._service?.options?.age_from;
    this.age_to = this._service?.options?.age_to;
    this.meetings_limit = this._service?.options?.meetings_limit;
    this.weekdays = this._service?.options?.weekdays;
    this.participants_limit = this._service?.participants_limit;

    if (this._service.school_id) {
      this.schoolService
        .getSchoolByIdOrSlug(this._service?.school_id, undefined, 'LOAD_TIME')
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (school) => {
            this.school = school;
            this.authorUrl = SchoolsService.schoolRoute(school, false);
            this.userAvatar = {
              type: 'avatar',
              img: FilesService.getFileUrlBySize(school?.cover_id, ImageSizeEnum.sm),
              title: school?.title,
              link: this.authorUrl.join('/'),
              form: 'none',
            };
            this.detectChanges();
          },
          error: (error) => {
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          },
        });
    }

    this.cover = {
      img: this.coverImageUrl,
      title: this.title,
      link: this.userServiceRoute,
      aspect: '16/9',
    };

    this.contentDate = this._service ? new Date(this._service.created_at) : new Date();

    this.prepareMetaImage();
    this.detectChanges();
  }

  public prepareAuthor(author: User) {
    this.authorUrl = AccountService.accountRoute(author);
    this.authorStats = author?.stats;
    this.userAvatar = {
      type: 'avatar',
      img: author?.__opts?.images.length
        ? this.prepareStorageUrl(author?.__opts?.images[0]?.sm)
        : FilesService.getFileUrlBySize(author.userpic_id, ImageSizeEnum.sm),
      title: AccountService.getUserFullName(author),
      link: this.authorUrl.join('/'),
      form: 'circle',
    };
    this.detectChanges();
  }

  public get isSingle(): boolean {
    return this.participants_limit === 1;
  }

  public isRegularClass(): boolean {
    return this.type === EUserServiceType.regular;
  }

  public priceWithoutDiscount(amount: number): number {
    return amount > 0 ? +amount / ((100 - this.discount) / 100) : null;
  }

  public getFormattedAmount(): Observable<string> {
    return this.billingV2Service.formatAmount$(this.service.price_tier);
  }

  public getFormattedAmountWithoutDiscount(): Observable<string> {
    return this.billingV2Service.formatAmount$(this.priceWithoutDiscount(this.service.price_tier));
  }

  public get me() {
    return this._me;
  }

  public get author(): User {
    return this._author;
  }

  public get isAuthor(): boolean {
    return this.me && this._service && this.me.id === this._service.author_id;
  }

  public get visit_info() {
    return this._service?.visit_info || {};
  }

  public formatAge(age: number) {
    return age > 65 ? '65+' : age;
  }

  public weekdaysToString(): string {
    return this.weekdays?.map((_d) => this.translate.instant(`days-of-week.${daysOfWeek[_d]}`)).join(', ');
  }

  public typeLabel(type: EUserServiceType): string {
    switch (type) {
      case EUserServiceType.single:
        return 'library.user-service-card.type.single';
      case EUserServiceType.regular:
        return 'library.user-service-card.type.regular';
      case EUserServiceType.mini:
        return 'library.user-service-card.type.mini';
      case EUserServiceType.course:
        return 'library.user-service-card.type.course';
      default:
        return '';
    }
  }

  public openCheckoutModal() {
    const onAuthComplete = () => {
      this.dialogsService.openUserServiceCheckoutWizard({
        userServiceId: this._service.id,
      });
      this.detectChanges();
    };
    if (this.isGuest) {
      this.authService.showAuthModal(onAuthComplete);
    } else {
      onAuthComplete();
    }
  }

  public onOpenCardInModal() {
    this.openUserServiceInModal.emit(this._service);
  }

  public get canEdit(): boolean {
    return this.mode === 'edit';
  }

  public get canSignup(): boolean {
    return this.mode === 'signup';
  }

  public onEdit() {
    this.selected.emit(true);
  }

  public onRemove() {
    this.userServicesService
      .removeUserService({ id: this._service.id })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.removed.emit(true);
          this.alertOpened = false;
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'SAVE-PROBLEM');
        },
      });
  }

  public get labelPeriod(): string {
    switch (this._service.type) {
      case EUserServiceType.single:
        return 'library.user-service-card.label-period.single';
      case EUserServiceType.regular:
        return 'library.user-service-card.label-period.regular';
      case EUserServiceType.mini:
        return 'library.user-service-card.label-period.mini';
      case EUserServiceType.course:
        return 'library.user-service-card.label-period.course';
    }
  }

  public printDebugInfo() {
    logger.log({
      service: this.service,
    });
  }

  public onFavoriteChanged() {
    this.favoriteChanged.emit(true);
  }

  private prepareMetaImage() {
    const metaImage = this._service.cover
      ? this._service.cover.lgX2 || this._service.cover.lg || this._service.cover.mdX2 || this._service.cover.md
      : null;
    this.metaImageUrl = metaImage ? this.assetsUrl(metaImage.url) : null;
  }
}
